import React, { useState, useEffect } from 'react'
import { useI18n } from '../../i18n'
import { useAuth } from '../../components/AuthProvider'
import { useNavigate } from 'react-router-dom'
import { API_ENDPOINTS } from '../../config/api'
import { BarChart3, TrendingUp, PieChart, FileText, Lightbulb, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react'
import { LineChart, Line, BarChart, Bar, PieChart as RechartsPieChart, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { PageLayout } from '../../layouts/PageLayout'
import { Button } from '../../components/ui/Button'

interface Recommendation {
  title: string
  description: string
  area: string
  priority: 'High' | 'Medium' | 'Low'
  expectedUplift: number
  suggestedActions: string[]
  relatedIds?: string[]
  count?: number
}

interface ComponentScores {
  policyLifecycle: number
  auditCoverage: number
  riskMaturity: number
  awarenessReadiness: number
  evidenceCoverage: number
}

interface ScoreAnalytics {
  governanceScore: number
  componentScores?: ComponentScores
  trend: Array<{ date: string; score: number }>
  byStandard: Array<{ standard: string; score: number }>
  byCategory: Array<{ category: string; score: number; count: number }>
  weakestAreas: Array<{ name: string; score: number; improvement: number }>
  recommendations?: Recommendation[]
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

type AnalyticsTab = 'standard' | 'national' | 'domain' | 'risk'

const GovernanceScoreAnalytics: React.FC = () => {
  const { t } = useI18n()
  const { token } = useAuth()
  const navigate = useNavigate()
  const [data, setData] = useState<ScoreAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedRecommendations, setExpandedRecommendations] = useState<Set<string>>(new Set())
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('standard')

  useEffect(() => {
    loadAnalytics()
  }, [])

  const loadAnalytics = async () => {
    try {
      setLoading(true)
      setError(null)
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 30000)
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_SCORE_ANALYTICS, {
        headers: { 'Authorization': `Bearer ${token}` },
        signal: controller.signal
      })
      clearTimeout(timeoutId)
      if (!res.ok) {
        const msg = res.status === 403
          ? t('governance.scoreAnalytics.error.forbidden', 'Access denied. You may need an assigned organization.')
          : res.status === 504
            ? t('governance.scoreAnalytics.error.timeout', 'Request timed out. Please try again.')
            : 'Failed to load score analytics'
        throw new Error(msg)
      }
      const result = await res.json()
      if (result.success && result.data) {
        const safeTrend = Array.isArray(result.data.trend)
          ? result.data.trend
              .map((item: any) => ({
                date: String(item?.date || ''),
                score: Number(item?.score || 0),
              }))
              .filter((item: any) => item.date && Number.isFinite(item.score))
          : []

        if (safeTrend.length > 0) {
          setData({ ...result.data, trend: safeTrend })
        } else {
          const readinessRes = await fetch(API_ENDPOINTS.GOVERNANCE_ANALYTICS_READINESS_HISTORY, {
            headers: { 'Authorization': `Bearer ${token}` },
          })
          let fallbackTrend: Array<{ date: string; score: number }> = []
          if (readinessRes.ok) {
            const readinessPayload = await readinessRes.json()
            fallbackTrend = Array.isArray(readinessPayload?.data)
              ? readinessPayload.data
                  .map((item: any) => ({
                    date: String(item?.date || ''),
                    score: Number(item?.value || 0),
                  }))
                  .filter((item: any) => item.date && Number.isFinite(item.score))
              : []
          }
          setData({ ...result.data, trend: fallbackTrend })
        }
      } else {
        throw new Error('Invalid response format')
      }
    } catch (err: any) {
      const message = err.name === 'AbortError'
        ? t('governance.scoreAnalytics.error.timeout', 'Request timed out. Please try again.')
        : (err.message || t('governance.scoreAnalytics.error.loadFailed', 'Failed to load score analytics'))
      setError(message)
      console.error('[GovernanceScoreAnalytics] Error:', err)
    } finally {
      setLoading(false)
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return 'bg-green-100'
    if (score >= 60) return 'bg-yellow-100'
    return 'bg-red-100'
  }

  if (loading) {
    return (
      <PageLayout pageScope="governance-score-analytics-page" title={t('governance.scoreAnalytics.title', 'Compliance Score Analytics')}>
        <p className="text-gray-600">{t('common.loading', 'Loading...')}</p>
      </PageLayout>
    )
  }

  if (error) {
    return (
      <PageLayout pageScope="governance-score-analytics-page" title={t('governance.scoreAnalytics.title', 'Compliance Score Analytics')}>
        <div className="p-4 bg-red-50 border border-red-200 rounded text-red-700 space-y-3">
          <p>{error}</p>
          <p className="text-sm text-gray-600">
            {t('governance.scoreAnalytics.error.retryHint', 'The request may have timed out. Check your connection and try again.')}
          </p>
          <Button
            type="button"
            size="small"
            color="primary"
            onClick={() => { setError(null); loadAnalytics(); }}
            className="rounded-md px-4 py-2 text-sm font-medium"
          >
            {t('common.retry', 'Retry')}
          </Button>
        </div>
      </PageLayout>
    )
  }

  if (!data) {
    return (
      <PageLayout pageScope="governance-score-analytics-page" title={t('governance.scoreAnalytics.title', 'Compliance Score Analytics')}>
        <p className="text-gray-600">{t('common.noData', 'No data available')}</p>
      </PageLayout>
    )
  }

  return (
    <PageLayout pageScope="governance-score-analytics-page" title={t('governance.scoreAnalytics.title', 'Compliance Score Analytics')}
      description={t('governance.scoreAnalytics.description', 'Governance maturity and compliance scoring analysis')}
    >
      {/* Overall Score Card */}
      <div className="mb-8">
        <div className={`ds-card p-6 ${getScoreBgColor(data.governanceScore)}`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold mb-2">
                {t('governance.scoreAnalytics.overallScore', 'Overall Governance Score')}
              </h2>
              <p className="text-gray-600 text-sm">
                {t('governance.scoreAnalytics.weightedAverage', 'Weighted average across all standards and categories')}
              </p>
            </div>
            <div className="text-center">
              <div className={`text-6xl font-bold ${getScoreColor(data.governanceScore)}`}>
                {data.governanceScore}
              </div>
              <div className="text-sm text-gray-600 mt-2">/ 100</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          <Button
            type="button"
            size="small"
            color="outline"
            onClick={() => setActiveTab('standard')}
            className={`border-0 border-b-2 rounded-none bg-transparent py-4 px-1 shadow-none font-medium text-sm ${
              activeTab === 'standard'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('governance.scoreAnalytics.tabs.byStandard', 'By Standard')}
          </Button>
          <Button
            type="button"
            size="small"
            color="outline"
            onClick={() => setActiveTab('national')}
            className={`border-0 border-b-2 rounded-none bg-transparent py-4 px-1 shadow-none font-medium text-sm ${
              activeTab === 'national'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('governance.scoreAnalytics.tabs.byNationalProfile', 'By National Profile')}
          </Button>
          <Button
            type="button"
            size="small"
            color="outline"
            onClick={() => setActiveTab('domain')}
            className={`border-0 border-b-2 rounded-none bg-transparent py-4 px-1 shadow-none font-medium text-sm ${
              activeTab === 'domain'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('governance.scoreAnalytics.tabs.byGovernanceDomain', 'By Governance Domain')}
          </Button>
          <Button
            type="button"
            size="small"
            color="outline"
            onClick={() => setActiveTab('risk')}
            className={`border-0 border-b-2 rounded-none bg-transparent py-4 px-1 shadow-none font-medium text-sm ${
              activeTab === 'risk'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('governance.scoreAnalytics.tabs.byRiskArea', 'By Risk Area')}
          </Button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'standard' && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Trend Chart */}
            <div className="ds-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold">
                  {t('governance.scoreAnalytics.trend', 'Score Trend')}
                </h3>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="score" stroke="#3b82f6" strokeWidth={2} name={t('governance.scoreAnalytics.score', 'Score')} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* By Standard Chart */}
            <div className="ds-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-green-600" />
                <h3 className="text-lg font-semibold">
                  {t('governance.scoreAnalytics.byStandard', 'Score by Standard')}
                </h3>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.byStandard}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="standard" angle={-45} textAnchor="end" height={80} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="score" fill="#10b981" name={t('governance.scoreAnalytics.score', 'Score')} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {activeTab === 'national' && (
        <div className="ds-card p-6">
          <h3 className="text-lg font-semibold mb-4">
            {t('governance.scoreAnalytics.byNationalProfile', 'Score by National Profile')}
          </h3>
          <p className="text-gray-600 mb-4">
            {t('governance.scoreAnalytics.nationalProfilesDescription', 'National compliance profiles contribute to overall governance score')}
          </p>
          {/* National profiles data would be loaded here */}
          <div className="text-center py-8 text-gray-500">
            {t('governance.scoreAnalytics.comingSoon', 'Coming soon - National profiles analytics')}
          </div>
        </div>
      )}

      {activeTab === 'domain' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* By Category (Governance Domain) */}
          <div className="ds-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <PieChart className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-semibold">
                {t('governance.scoreAnalytics.byCategory', 'Score by Category')}
              </h3>
            </div>
            <div className="space-y-3">
              {data.byCategory.map((item, index) => (
                <div key={item.category} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{item.category}</span>
                      <span className={`font-bold ${getScoreColor(item.score)}`}>
                        {item.score}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${item.score >= 80 ? 'bg-green-600' : item.score >= 60 ? 'bg-yellow-600' : 'bg-red-600'}`}
                        style={{ width: `${item.score}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {item.count} {t('governance.scoreAnalytics.items', 'items')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Component Scores Breakdown */}
          {data.componentScores && (
            <div className="ds-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold">
                  {t('governance.scoreAnalytics.componentScores', 'Component Scores Breakdown')}
                </h3>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Policy Lifecycle (40%)</span>
                    <span className={`font-bold ${getScoreColor(data.componentScores.policyLifecycle)}`}>
                      {data.componentScores.policyLifecycle}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${data.componentScores.policyLifecycle}%` }} />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Audit Coverage (25%)</span>
                    <span className={`font-bold ${getScoreColor(data.componentScores.auditCoverage)}`}>
                      {data.componentScores.auditCoverage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: `${data.componentScores.auditCoverage}%` }} />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Risk Maturity (20%)</span>
                    <span className={`font-bold ${getScoreColor(data.componentScores.riskMaturity)}`}>
                      {data.componentScores.riskMaturity}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-yellow-600 h-2 rounded-full" style={{ width: `${data.componentScores.riskMaturity}%` }} />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Awareness Readiness (10%)</span>
                    <span className={`font-bold ${getScoreColor(data.componentScores.awarenessReadiness)}`}>
                      {data.componentScores.awarenessReadiness}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-600 h-2 rounded-full" style={{ width: `${data.componentScores.awarenessReadiness}%` }} />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Evidence Coverage (5%)</span>
                    <span className={`font-bold ${getScoreColor(data.componentScores.evidenceCoverage)}`}>
                      {data.componentScores.evidenceCoverage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-orange-600 h-2 rounded-full" style={{ width: `${data.componentScores.evidenceCoverage}%` }} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'risk' && (
        <div className="ds-card p-6">
          <h3 className="text-lg font-semibold mb-4">
            {t('governance.scoreAnalytics.byRiskArea', 'Score by Risk Area')}
          </h3>
          <p className="text-gray-600 mb-4">
            {t('governance.scoreAnalytics.riskAreaDescription', 'Risk-based scoring across different risk areas')}
          </p>
          {/* Weakest Areas */}
          <div className="mt-6">
            <h4 className="font-semibold mb-4">
              {t('governance.scoreAnalytics.weakestAreas', 'Weakest Areas')}
            </h4>
            <div className="space-y-4">
              {data.weakestAreas.map((area) => (
                <div key={area.name} className="border-b pb-3 last:border-0">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{area.name}</span>
                    <span className={`font-bold ${getScoreColor(area.score)}`}>
                      {area.score}%
                    </span>
                  </div>
                  <div className="text-sm text-green-600">
                    {t('governance.scoreAnalytics.potentialImprovement', 'Potential improvement')}: +{area.improvement}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* AI Recommendations */}
      {data.recommendations && data.recommendations.length > 0 && (
        <div className="ds-card p-6 mt-8">
          <div className="flex items-center gap-2 mb-6">
            <Lightbulb className="w-5 h-5 text-yellow-600" />
            <h3 className="text-lg font-semibold">
              {t('governance.scoreAnalytics.recommendations', 'AI Recommendations')}
            </h3>
          </div>
          <div className="space-y-4">
            {data.recommendations.map((rec, index) => {
              const recId = `rec-${index}`
              const isExpanded = expandedRecommendations.has(recId)
              const priorityColor = rec.priority === 'High' ? 'bg-red-100 text-red-800 border-red-300' :
                                   rec.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                                   'bg-blue-100 text-blue-800 border-blue-300'

              const getAreaRoute = (area: string) => {
                switch (area) {
                  case 'policies': return '/governance/policies'
                  case 'audits': return '/governance/audits'
                  case 'risks': return '/governance/risks'
                  case 'training': return '/governance/training'
                  case 'evidence': return '/governance/evidence'
                  default: return '/governance/overview'
                }
              }

              return (
                <div key={recId} className="border rounded-lg overflow-hidden">
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-50 flex items-start justify-between"
                    onClick={() => {
                      const newExpanded = new Set(expandedRecommendations)
                      if (isExpanded) {
                        newExpanded.delete(recId)
                      } else {
                        newExpanded.add(recId)
                      }
                      setExpandedRecommendations(newExpanded)
                    }}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold">{rec.title}</h4>
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${priorityColor}`}>
                          {rec.priority}
                        </span>
                        {rec.count !== undefined && (
                          <span className="text-xs text-gray-500">
                            ({rec.count} {t('governance.scoreAnalytics.items', 'items')})
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{rec.description}</p>
                      <div className="mt-2 text-sm text-green-600 font-medium">
                        {t('governance.scoreAnalytics.expectedUplift', 'Expected improvement')}: +{rec.expectedUplift}%
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        type="button"
                        size="small"
                        color="outline"
                        iconOnly
                        icon={<ExternalLink className="w-4 h-4" />}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(getAreaRoute(rec.area))
                        }}
                        className="border-0 bg-transparent p-1 text-blue-600 hover:bg-blue-50 rounded"
                        title={t('governance.scoreAnalytics.goToArea', 'Go to area')}
                      />
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="p-4 bg-gray-50 border-t">
                      <h5 className="font-medium mb-3">
                        {t('governance.scoreAnalytics.suggestedActions', 'Suggested Actions')}:
                      </h5>
                      <ul className="space-y-2">
                        {rec.suggestedActions.map((action, actionIndex) => (
                          <li key={actionIndex} className="flex items-start gap-2">
                            <span className="text-blue-600 mt-1">•</span>
                            <span className="text-sm text-gray-700">{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </PageLayout>
  )
}

export default GovernanceScoreAnalytics

